// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the lesson library in the browser (Education tier plan F6/F7):
 * the cards and the lessons from `/api/lessons` (an account whose plan
 * includes the lessons), what each step's dataset is on this site
 * (`resolveStepDataset`), and the teacher's own copy of a canvas.
 *
 * A lesson is a canvas (owner, 2026-10-05): it never moves the globe by
 * itself. What a teacher sets on a step (the camera, the date) is kept in
 * this browser as their copy (`ayni-lesson-copies.v1`), replayed when they
 * come back to that step, and downloaded as their own Khipu
 * (`adaptLesson`) in one `.zip`. Nothing about the teacher or the class is
 * sent anywhere.
 */
import type { Khipu, KhipuStep, KhipuText, LessonSummary, StepSetting } from './khipu'
import { adaptLesson, exchangeKhipu, localized, newKhipuId } from './khipu'
import { LIBRARY_PREFIX, libraryDatasetId, libraryIdForLegacy, loadLessonLibrary } from './lessonLibrary'
import { isRightsHeld } from './rightsHold'

export interface LessonCatalog {
  units: Record<string, string>
  lessons: LessonSummary[]
}

export type LessonFetchError = 'signed_out' | 'plan_required' | 'not_found' | 'unavailable'

export type LessonResult<T> = { ok: true; value: T } | { ok: false; error: LessonFetchError }

function errorFor(status: number): LessonFetchError {
  if (status === 401) return 'signed_out'
  if (status === 402 || status === 403) return 'plan_required'
  if (status === 404) return 'not_found'
  return 'unavailable'
}

let catalogCache: LessonCatalog | null = null

/** The lesson cards; kept for the page's life once read. */
export async function fetchLessonCatalog(fetchImpl: typeof fetch = fetch): Promise<LessonResult<LessonCatalog>> {
  if (catalogCache) return { ok: true, value: catalogCache }
  try {
    const res = await fetchImpl('/api/lessons', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    if (!res.ok) return { ok: false, error: errorFor(res.status) }
    catalogCache = (await res.json()) as LessonCatalog
    return { ok: true, value: catalogCache }
  } catch {
    return { ok: false, error: 'unavailable' }
  }
}

/** Forget the cards (the account changed). */
export function forgetLessonCatalog(): void {
  catalogCache = null
}

export async function fetchLesson(slug: string, fetchImpl: typeof fetch = fetch): Promise<LessonResult<Khipu>> {
  try {
    const res = await fetchImpl(`/api/lessons/${encodeURIComponent(slug)}`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
    if (!res.ok) return { ok: false, error: errorFor(res.status) }
    const body = (await res.json()) as { khipu: Khipu }
    return { ok: true, value: body.khipu }
  } catch {
    return { ok: false, error: 'unavailable' }
  }
}

/** What a step shows on this site. */
export type StepDataset =
  | { kind: 'none' }
  | { kind: 'stream' | 'library' | 'app'; datasetId: string; title: string; units?: string; dataEnabled: boolean }
  | { kind: 'unavailable'; title: string }

const STREAM_PREFIX = 'R2_DASH_'

/**
 * A step's dataset as this app names it: an AYNI stream is its index row
 * (`R2_DASH_<dsa id>`), a library dataset its `LIB_<id>` row when the
 * library can play it; anything else keeps its name, marked unavailable.
 */
export async function resolveStepDataset(k: Khipu, step: KhipuStep, lang = 'en'): Promise<StepDataset> {
  return step.dataset ? resolveDatasetKey(k, step.dataset, lang) : { kind: 'none' }
}

/**
 * AYNI (2026-10-06): the lesson's datasets no step uses, in their order:
 * extras the teacher can put on the globe when a note suggests it (a
 * plate-boundary map, a model of the ocean conveyor, a narrated explainer).
 */
export function lessonExtras(k: Khipu): Array<{ key: string; title: string }> {
  // A step's real-time variables are offered on that step (`stepRealtime`), not here.
  const used = new Set([
    ...k.path.steps.map((s) => s.dataset).filter(Boolean),
    ...k.path.steps.flatMap((s) => (Array.isArray(s.layers) ? s.layers.map((l) => l.dataset) : [])),
  ])
  return k.datasets
    // A layer (`layer:<id>`) goes over a step's dataset (`stepOverlays`), never in its place.
    .filter((d) => !used.has(d.key) && !d.ref.startsWith(LAYER_PREFIX))
    .map((d) => ({ key: d.key, title: localized(d.title as KhipuText | undefined) || d.ref }))
}

const LAYER_PREFIX = 'layer:'
const APP_PREFIX = 'app:'
/** NOAA's old catalogue ids, from the list AYNI no longer reads. */
const LEGACY_NOAA_ID = /^(INTERNAL_SOS_|SOS_ONLY_)/
const TINTS = new Set(['white', 'black', 'source'])

/** An overlay a lesson step suggests: a layer from `/api/layers`, by id. */
export interface StepOverlay {
  layerId: string
  title: string
  tint?: 'white' | 'black' | 'source'
}

/**
 * AYNI (owner, 2026-10-06): the overlays a step suggests over its dataset
 * (`layers`, role `overlay`, naming a `layer:<id>` dataset): plate
 * boundaries over earthquakes, rivers over rainfall. Suggestions only: the
 * lessons leave the layers to the teacher ("layers" stays open), so the
 * player offers them as one-press buttons and never puts one on by itself.
 */
export function stepOverlays(k: Khipu, step: KhipuStep, lang = 'en'): StepOverlay[] {
  const layers = Array.isArray(step.layers) ? (step.layers as Array<{ role?: unknown; dataset?: unknown; tint?: unknown }>) : []
  return layers.flatMap((l): StepOverlay[] => {
    if (l.role !== 'overlay') return []
    const ref = k.datasets.find((d) => d.key === l.dataset)
    if (!ref || !ref.ref.startsWith(LAYER_PREFIX)) return []
    const tint = typeof l.tint === 'string' && TINTS.has(l.tint) ? (l.tint as StepOverlay['tint']) : undefined
    return [{ layerId: ref.ref.slice(LAYER_PREFIX.length), title: localized(ref.title as KhipuText | undefined, lang) || ref.ref, ...(tint ? { tint } : {}) }]
  })
}

/** A real-time variable a lesson step offers to add over its dataset. */
export interface StepRealtime {
  /** The stream, as this app names it (`R2_DASH_<id>`). */
  datasetId: string
  title: string
  /** What to look for once it is on; '' when the lesson says nothing. */
  prompt: string
}

/** Most real-time variables a step offers (as many as a computer plays at once). */
export const STEP_MAX_REALTIME = 4

/**
 * AYNI (2026-10-09): the real-time variables a lesson step offers over its
 * dataset (`layers`, role `realtime`, naming a `stream:` dataset), in the
 * order the lesson reveals them: cloud, then rain, then lightning over the
 * sea's temperature. Each comes with what to look for once it is on, the
 * question that connects it with the step's dataset. Like the overlays,
 * suggestions only: the teacher adds each with one press, never the lesson.
 */
export function stepRealtime(k: Khipu, step: KhipuStep, lang = 'en'): StepRealtime[] {
  const layers = Array.isArray(step.layers) ? step.layers : []
  const own = k.datasets.find((d) => d.key === step.dataset)
  const seen = new Set<string>()
  return layers
    .flatMap((l): StepRealtime[] => {
      if (l.role !== 'realtime') return []
      const ref = k.datasets.find((d) => d.key === l.dataset)
      if (!ref || !ref.ref.startsWith('stream:') || !ref.dsaId || ref === own) return []
      const datasetId = STREAM_PREFIX + ref.dsaId
      if (seen.has(datasetId)) return []
      seen.add(datasetId)
      return [{ datasetId, title: localized(ref.title as KhipuText | undefined, lang) || ref.ref, prompt: localized(l.prompt, lang) }]
    })
    .slice(0, STEP_MAX_REALTIME)
}

/** One of the lesson's datasets, by its key, as this app names it. */
export async function resolveDatasetKey(k: Khipu, key: string, lang = 'en'): Promise<StepDataset> {
  const ref = k.datasets.find((d) => d.key === key)
  if (!ref || ref.ref.startsWith(LAYER_PREFIX)) return { kind: 'none' }
  const title = localized(ref.title as KhipuText | undefined, lang) || ref.ref
  const extra = { ...(ref.units ? { units: ref.units } : {}), dataEnabled: ref.dataEnabled === true }
  // A stream held for rights (`rightsHold.ts`) is not in the catalogue, so the step says so.
  if (ref.ref.startsWith('stream:') && isRightsHeld(ref.ref.slice('stream:'.length))) return { kind: 'unavailable', title }
  if (ref.ref.startsWith('stream:') && ref.dsaId) return { kind: 'stream', datasetId: STREAM_PREFIX + ref.dsaId, title, ...extra }
  // AYNI (2026-10-07): a row of this app's catalogue by its id (a NOAA story's dataset, a session's).
  if (ref.ref.startsWith(APP_PREFIX)) {
    const appId = typeof ref.appId === 'string' && ref.appId ? ref.appId : ref.ref.slice(APP_PREFIX.length)
    // AYNI (own catalogue, 2026-10-07): NOAA's old catalogue ids now name a
    // row of AYNI's NOAA library, or nothing when the library lacks it.
    if (LEGACY_NOAA_ID.test(appId)) {
      const libId = await libraryIdForLegacy(appId)
      return libId ? { kind: 'library', datasetId: libId, title, ...extra } : { kind: 'unavailable', title }
    }
    return { kind: 'app', datasetId: appId, title, ...extra }
  }
  if (ref.ref.startsWith('sos:')) {
    const sosId = ref.ref.slice(4)
    const library = await loadLessonLibrary()
    if (Object.prototype.hasOwnProperty.call(library.datasets, sosId)) return { kind: 'library', datasetId: libraryDatasetId(sosId), title, ...extra }
  }
  return { kind: 'unavailable', title }
}

export function isLibraryDatasetId(id: string): boolean {
  return id.startsWith(LIBRARY_PREFIX)
}

/** A step's prompts for one audience, in their order. */
export function promptsFor(step: KhipuStep, audience: 'students' | 'presenter'): Array<{ kind: string; text: string }> {
  return (step.prompts ?? [])
    .filter((p) => (p.audience ?? 'everyone') === audience || (audience === 'students' && p.audience === 'everyone'))
    .map((p) => ({ kind: p.kind, text: localized(p.text) }))
}

// --------------------------------------------------------------- the teacher's copy

const COPIES_KEY = 'ayni-lesson-copies.v1'

interface CopyStore {
  [khipuId: string]: { steps: Record<string, StepSetting>; updatedAt: string }
}

function readCopies(storage: Storage | null): CopyStore {
  try {
    const raw = storage?.getItem(COPIES_KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : null
    return parsed && typeof parsed === 'object' ? (parsed as CopyStore) : {}
  } catch {
    return {}
  }
}

function writeCopies(storage: Storage | null, copies: CopyStore): void {
  try {
    storage?.setItem(COPIES_KEY, JSON.stringify(copies))
  } catch {
    /* private mode or full: the copy lasts this page only */
  }
}

const defaultStorage = (): Storage | null => (typeof localStorage === 'undefined' ? null : localStorage)

/** What the teacher set on each step of this lesson, in this browser. */
export function lessonCopy(khipuId: string, storage: Storage | null = defaultStorage()): Record<string, StepSetting> {
  return readCopies(storage)[khipuId]?.steps ?? {}
}

/** Keep (or with `null`, forget) what the teacher set on one step. */
export function setStepSetting(khipuId: string, stepId: string, setting: StepSetting | null, now = new Date(), storage: Storage | null = defaultStorage()): void {
  const copies = readCopies(storage)
  const steps = { ...(copies[khipuId]?.steps ?? {}) }
  if (setting && (setting.view || setting.time)) steps[stepId] = setting
  else delete steps[stepId]
  if (Object.keys(steps).length) copies[khipuId] = { steps, updatedAt: now.toISOString() }
  else delete copies[khipuId]
  writeCopies(storage, copies)
}

const OWN_KEY = 'ayni-lesson-own.v1'

/**
 * The id of this browser's own lesson made from an AYNI canvas, the same
 * every time, so saving it again replaces it rather than adding another.
 */
export function ownLessonId(canvasId: string, storage: Storage | null = defaultStorage()): string {
  let ids: Record<string, string> = {}
  try {
    const raw = storage?.getItem(OWN_KEY)
    const parsed = raw ? (JSON.parse(raw) as unknown) : null
    if (parsed && typeof parsed === 'object') ids = parsed as Record<string, string>
  } catch {
    /* start again */
  }
  const known = ids[canvasId]
  if (typeof known === 'string' && /^[a-f0-9]{16}$/.test(known)) return known
  const id = newKhipuId()
  try {
    storage?.setItem(OWN_KEY, JSON.stringify({ ...ids, [canvasId]: id }))
  } catch {
    /* this page only */
  }
  return id
}

/** Whether a lesson is a teacher's own (made from a canvas), not an AYNI canvas. */
export function isOwnLesson(k: Khipu): boolean {
  return (k.derivedFrom ?? []).some((d) => d.relation === 'adapted-from')
}

/**
 * The teacher's own lesson with what they kept on each step: from an AYNI
 * canvas, a new Khipu adapted from it (the same id every time in this
 * browser); from their own lesson, that lesson brought up to date, keeping
 * its id, origin and creation date.
 */
export function ownLesson(k: Khipu, settings: Record<string, StepSetting>, opts: { now?: Date; creator?: string; storage?: Storage | null } = {}): Khipu {
  const now = (opts.now ?? new Date()).toISOString().replace(/\.\d{3}Z$/, 'Z')
  if (!isOwnLesson(k)) return adaptLesson(k, settings, { id: ownLessonId(k.id, opts.storage ?? defaultStorage()), now, creator: opts.creator })
  const updated = adaptLesson(k, settings, { id: k.id, now, creator: opts.creator ?? k.creator })
  return { ...updated, slug: k.slug, created: k.created, derivedFrom: k.derivedFrom }
}

/** The licence notice that goes with every lesson AYNI provides (owner, 2026-10-05, wording approved the same day). */
export const LESSON_NOTICE = [
  'This lesson is an AYNI lesson by Pachamama Studios B.V. (AYNI), licensed under',
  'Creative Commons Attribution-NonCommercial-ShareAlike 4.0 (CC BY-NC-SA 4.0):',
  'https://creativecommons.org/licenses/by-nc-sa/4.0/',
  '',
  'It may not be used commercially, or in promotion by any company or',
  'organisation, without the written permission of Pachamama Studios B.V.',
  'Adaptations keep this licence and credit Pachamama Studios B.V. (AYNI).',
  '',
].join('\n')

/**
 * The teacher's copy as one `.zip`: their own Khipu (`<slug>-adapted.kpu`)
 * and the licence notice.
 */
export async function buildLessonZip(k: Khipu, settings: Record<string, StepSetting>, opts: { now?: Date; creator?: string; storage?: Storage | null } = {}): Promise<{ blob: Blob; filename: string }> {
  return zipKhipu(ownLesson(k, settings, opts))
}

/**
 * The notice in a Khipu's `.zip`: AYNI's lesson notice for a lesson; for
 * any other kind, its title, credit and licence as the Khipu states them.
 */
export function khipuNotice(k: Khipu): string {
  if (k.kind === 'lesson') return LESSON_NOTICE
  const licence = k.license ? [k.license.name ?? k.license.spdx, k.license.url].filter(Boolean).join(': ') : ''
  return [
    localized(k.title),
    ...(k.attribution ? [`Credit: ${k.attribution}`] : k.creator ? [`Made by: ${k.creator}`] : []),
    ...(licence ? [`Licence: ${licence}`] : []),
    '',
    'An AYNI Khipu (.kpu), saved with AYNI XR (https://vr.ayni.eu.com).',
    '',
  ].join('\n')
}

/**
 * A Khipu as one `.zip`: `<slug>.kpu`, its CSVs, and the licence notice.
 * `exchange`: the minimal exchange copy instead of the full Khipu, and no
 * CSVs (owner, 2026-10-05: what a plan that keeps no Khipus downloads).
 */
export async function zipKhipu(k: Khipu, files: Array<{ name: string; body: string }> = [], opts: { exchange?: boolean } = {}): Promise<{ blob: Blob; filename: string }> {
  const slug = k.slug ?? k.id
  const { default: JSZip } = await import('jszip')
  const zip = new JSZip()
  zip.file(`${slug}.kpu`, `${JSON.stringify(opts.exchange ? exchangeKhipu(k) : k, null, 2)}\n`)
  if (!opts.exchange) for (const f of files) zip.file(f.name, f.body)
  zip.file('LICENSE.txt', khipuNotice(k))
  const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/zip' })
  return { blob, filename: `ayni-${k.kind === 'lesson' ? 'lesson' : 'khipu'}-${slug}.zip` }
}
